
import { createClient } from '@supabase/supabase-js';
import * as Sentry from '@/lib/sentry-shim';
import type { Database } from './database.types';

// Supabase credentials - MUST use environment variables
// Note: Supabase anon keys are PUBLIC and safe to expose in frontend code
// BUT should still be managed via environment variables to:
// 1. Keep them out of source control
// 2. Allow easy rotation without code changes
// 3. Support different environments (dev/staging/prod)

// Get credentials from environment variables - NO hardcoded fallbacks
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate that credentials are provided
if (!supabaseUrl || !supabaseAnonKey) {
  const missing = [];
  if (!supabaseUrl) missing.push('VITE_SUPABASE_URL');
  if (!supabaseAnonKey) missing.push('VITE_SUPABASE_ANON_KEY');
  
  throw new Error(
    `❌ CRITICAL: Missing required environment variables: ${missing.join(', ')}\n\n` +
    `These must be set in:\n` +
    `  • Local: .env file in project root\n` +
    `  • CI/CD: GitHub Actions secrets\n` +
    `  • Deployment: Platform environment variables (Vercel, Netlify, etc.)\n\n` +
    `Example .env file:\n` +
    `VITE_SUPABASE_URL=https://your-project.supabase.co\n` +
    `VITE_SUPABASE_ANON_KEY=your-anon-key-here\n\n` +
    `See .env.example for reference.`
  );
}

// Export URL and key for use in function calls
export const SUPABASE_URL = supabaseUrl;
export const SUPABASE_ANON_KEY = supabaseAnonKey;

// Create Supabase client
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey);

async function getMonthlyPropertyCount(): Promise<number> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  const resp = await fetch(`${SUPABASE_URL}/functions/v1/collab-rpc`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session?.access_token ?? ''}`,
    },
    body: JSON.stringify({ action: 'get_monthly_property_count' }),
  });

  const body = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    throw new Error(body?.message ?? body?.error ?? `HTTP ${resp.status}`);
  }

  return Number(body?.data ?? 0);
}

// Updated to always return false, taking the app out of development mode
export const isMissingCredentials = () => false;

export const checkAuthConnection = async () => {
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      return false;
    }
    return true;
  } catch (err) {
    return false;
  }
};

// Tier property limits mapping (matches backend enforcement)
export const TIER_PROPERTY_LIMITS: Record<string, number | null> = {
  free: 1,
  solo: null,
  pro: null,
  enterprise: 0,
};

// Get property limit for a tier
export const getPropertyLimitByTier = (tier: string | null | undefined): number | null => {
  if (!tier) return 1; // Default to free tier
  const limit = TIER_PROPERTY_LIMITS[tier.toLowerCase()];
  return limit === undefined ? 1 : limit;
};

// Check if user has reached property limit (deprecated - use checkPropertyQuota instead)
export const hasReachedPropertyLimit = async () => {
  return false;
};

// Check if user can create a new property.
// Reads tier/status from public.subscriptions (source of truth). No row =
// free/inactive. Paid tiers require status IN ('active', 'trialing').
export const checkPropertyQuota = async (): Promise<{
  canCreate: boolean;
  currentCount: number;
  limit: number | null;
  tier: string | null;
}> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { canCreate: false, currentCount: 0, limit: 1, tier: 'free' };
    }

    const { data: subscription, error: subError } = await supabase
      .from('subscriptions')
      .select('tier, status')
      .eq('user_id', user.id)
      .maybeSingle();

    if (subError) {
      Sentry.addBreadcrumb({
        category: 'subscriptions',
        message: 'subscription_read_failed_defaulted_free',
        level: 'info',
        data: { error: subError.message },
      });
    }

    const tier = (subscription as any)?.tier ?? 'free';
    const status = (subscription as any)?.status ?? 'inactive';
    const limit = getPropertyLimitByTier(tier);

    // Paid tiers require active or trialing status. Free tier (including
    // users with no subscription row) can still create up to their limit.
    if (tier !== 'free' && status !== 'active' && status !== 'trialing') {
      return { canCreate: false, currentCount: 0, limit, tier };
    }

    // Unlimited tier (pro).
    if (limit === null) {
      return { canCreate: true, currentCount: 0, limit: null, tier };
    }

    const currentCount = await getMonthlyPropertyCount();
    const canCreate = currentCount < limit;

    return { canCreate, currentCount, limit, tier };
  } catch (error) {
    console.error('Error checking property quota:', error);
    return { canCreate: false, currentCount: 0, limit: 1, tier: 'free' };
  }
};

// Get quota usage for display (used in header)
export const getQuotaUsage = async (): Promise<{
  current: number;
  limit: number | null;
  tier: string | null;
  percentage: number;
}> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { current: 0, limit: 1, tier: 'free', percentage: 0 };
    }

    // Source of truth for tier: public.subscriptions. No row = free tier.
    const { data: subscription, error: subError } = await supabase
      .from('subscriptions')
      .select('tier')
      .eq('user_id', user.id)
      .maybeSingle();

    if (subError) {
      Sentry.addBreadcrumb({
        category: 'subscriptions',
        message: 'subscription_read_failed_defaulted_free',
        level: 'info',
        data: { error: subError.message },
      });
    }

    const tier = (subscription as any)?.tier ?? 'free';
    const limit = getPropertyLimitByTier(tier);

    // If unlimited, return special values
    if (limit === null) {
      return { current: 0, limit: null, tier, percentage: 0 };
    }

    // Get current month's property count
    const current = await getMonthlyPropertyCount();
    const percentage = limit > 0 ? Math.min((current / limit) * 100, 100) : 0;

    return { current, limit, tier, percentage };
  } catch (error) {
    console.error('Error getting quota usage:', error);
    return { current: 0, limit: 3, tier: 'free', percentage: 0 };
  }
};
